/**
 * Pilcore PMS - Main Guest Create Customer Popup
 * Same UX as BookerCustomerCreatePopup, but assigns customer to reservation_draft_guests as main guest.
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import GuestProfileDialog from "./GuestProfileDialog";

const MainGuestCustomerCreatePopup = ({
  show,
  businessUnitId,
  reservationDraftId,
  reservationId = null,
  onClose,
  mode = "create", // "create" | "edit"
  initialData = null,
  onSaved,
}) => {
  const [contentReady, setContentReady] = useState(false);
  const [requiredFields, setRequiredFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const busyRef = useRef(false);

  const [formData, setFormData] = useState({
    salutation: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    second_last_name: "",
    gender: "",
    birth_date: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    address: "",
    postal_code: "",
    document_type: "",
    document_number: "",
    is_vip: false,
    vip_level: "",
    customer_description: "",
    photo_url: "",
    status: "active",
  });

  useEffect(() => {
    if (!show) return;
    setFeedback("");

    if (mode === "edit" && initialData) {
      console.log("[DEBUG][MAIN_GUEST][INITIAL_DATA_FULL]", initialData);
      console.log("[DEBUG][MAIN_GUEST][INITIAL_COUNTRY]", initialData.country);
      console.log("[MAIN_GUEST_POPUP][INIT][EDIT] loading initialData", { id: initialData.id });
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        document_type: initialData.document_type ?? "",
        document_number: initialData.document_number ?? "",
        salutation: initialData.salutation ?? "",
        gender: initialData.gender ?? "",
        vip_level: initialData.vip_level ?? "",
        birth_date: initialData.birth_date ?? "",
      }));
    }

    if (mode === "create") {
      console.log("[MAIN_GUEST_POPUP][INIT][CREATE] reset empty form");
      setFormData({
        salutation: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        second_last_name: "",
        gender: "",
        birth_date: "",
        email: "",
        phone: "",
        country: "",
        city: "",
        address: "",
        postal_code: "",
        document_type: "",
        document_number: "",
        is_vip: false,
        vip_level: "",
        customer_description: "",
        photo_url: "",
        status: "active",
      });
    }
  }, [show, mode, initialData]);

  useEffect(() => {
    if (!show) return;

    setContentReady(false);

    async function loadEverything() {
      try {
        console.log("[MainGuestCreatePopup][LOAD] hotel settings", { businessUnitId });
        const resolvedBusinessUnitId = businessUnitId || initialData?.business_unit_id || null;

        if (!resolvedBusinessUnitId) {
          console.warn("[MainGuestCreatePopup][LOAD] Missing businessUnitId; required fields not loaded.");
          setRequiredFields([]);
          return;
        }

        setFormData((prev) => ({
          ...prev,
          business_unit_id: resolvedBusinessUnitId,
        }));

        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
          params: { business_unit_id: resolvedBusinessUnitId },
        });

        const raw = res.data?.required_customer_fields_reservation ?? null;

        let parsed = null;
        if (typeof raw === "string") {
          try {
            parsed = JSON.parse(raw);
          } catch (e) {
            console.warn("[MainGuestCreatePopup][SETTINGS] JSON parse failed", { raw });
          }
        } else {
          parsed = raw;
        }

        let rf = [];
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          rf = Array.isArray(parsed.main_guest) ? parsed.main_guest : [];
        } else if (Array.isArray(parsed)) {
          rf = parsed;
        }

        console.log("[MainGuestCreatePopup][SETTINGS] requiredFields(main_guest)", rf);
        setRequiredFields(rf);
      } catch (err) {
        console.error("[MainGuestCreatePopup][LOAD][ERR]", err);
        setRequiredFields([]);
      } finally {
        setContentReady(true);
      }
    }

    loadEverything();
  }, [show, businessUnitId, initialData?.business_unit_id]);

  if (!show) return null;
  if (!contentReady) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback("");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selected, action) => {
    const name = action.name;
    const value = selected ? selected.value : "";
    setFeedback("");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const normalizePayload = () => ({
    ...formData,
    document_type: formData.document_type === "" ? null : formData.document_type,
    document_number: formData.document_number === "" ? null : formData.document_number,
    salutation: formData.salutation === "" ? null : formData.salutation,
    gender: formData.gender === "" ? null : formData.gender,
    vip_level: formData.vip_level === "" ? null : formData.vip_level,
    birth_date: isNaN(new Date(formData.birth_date).getTime()) ? null : formData.birth_date,
  });

  const getMissing = () =>
    (requiredFields || []).filter((f) => {
      const v = formData?.[f];
      return v == null || String(v).trim() === "";
    });

  const assignmentTargetId = reservationId || reservationDraftId;
  const usesRealReservation = Boolean(reservationId);

  const assignMainGuest = async (customerId) => {
    if (usesRealReservation) {
      await axios.post("/api/reservation-guests", {
        reservation_id: reservationId,
        customer_id: customerId,
        is_main_guest: true,
        is_child: false,
        relationship: null,
        guest_index: null,
        updated_by: "MainGuestPopupCreate",
      });
      return;
    }

    await axios.post("/api/reservation-drafts/draft-guests", {
      reservation_draft_id: reservationDraftId,
      customer_id: customerId,
      is_main_guest: true,
      is_child: false,
      relationship: null,
      guest_index: null,
      updated_by: "MainGuestPopupCreate",
    });
  };

  const removeMainGuest = async () => {
    if (usesRealReservation) {
      await axios.delete(`/api/reservation-guests/by-reservation/${reservationId}/main-guest`);
      return;
    }

    await axios.delete(
      `/api/reservation-drafts/draft-guests/by-draft/${reservationDraftId}/main-guest`
    );
  };

  const finishAndClose = async () => {
    if (typeof onSaved === "function") {
      await onSaved();
    } else if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (busyRef.current) return;

    const missing = getMissing();
    if (missing.length > 0) {
      setFeedback({ key: "Complete the required fields: {fields}.", fields: missing });
      return;
    }

    if (!assignmentTargetId && mode === "create") {
      setFeedback("Missing reservation id.");
      return;
    }

    if (mode === "edit" && !initialData?.id) {
      setFeedback("Missing customer id for edit.");
      return;
    }

    busyRef.current = true;
    setSaving(true);
    setFeedback("");

    try {
      const payload = normalizePayload();
      if (mode === "create") {
        const { data: created } = await axios.post("/api/customers", payload);
        const newCustomerId = created?.id;

        if (!newCustomerId) {
          setFeedback("Customer created but no id returned.");
          return;
        }

        await assignMainGuest(newCustomerId);
      } else {
        await axios.put(`/api/customers/${initialData.id}`, payload);
      }

      await finishAndClose();
    } catch (err) {
      console.error("[MAIN_GUEST_POPUP][SAVE][ERR]", err);
      setFeedback("Error saving main guest. Please try again.");
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (busyRef.current) return;
    if (!assignmentTargetId) {
      setFeedback("Missing reservation id.");
      return;
    }

    busyRef.current = true;
    setRemoving(true);
    setFeedback("");

    try {
      await removeMainGuest();
      await finishAndClose();
    } catch (err) {
      console.error("[MAIN_GUEST_POPUP][REMOVE][ERR]", err);
      setFeedback("Error removing main guest. Please try again.");
    } finally {
      busyRef.current = false;
      setRemoving(false);
    }
  };

  return (
    <GuestProfileDialog
      title={mode === "edit" ? "Edit Main Guest" : "Create Main Guest"}
      subtitle={mode === "edit"
        ? "Update the selected main guest details while keeping the reservation assignment in sync."
        : "Create a new guest profile and assign it directly as the main guest of the reservation."}
      contextLabel={mode === "edit" ? "Main guest details" : "New reservation main guest"}
      createLabel="Create main guest"
      removeLabel="Remove main guest"
      mode={mode}
      formData={formData}
      handleChange={handleChange}
      handleSelectChange={handleSelectChange}
      requiredFields={requiredFields}
      onClose={onClose}
      onSubmit={handleSave}
      onRemove={handleRemove}
      saving={saving}
      removing={removing}
      feedback={feedback}
    />
  );
};

export default MainGuestCustomerCreatePopup;
